// frontend/src/components/HmrcReferencePanel.jsx
// Project: Vision ANPR by Castle Traffic Ltd
// Author: Castle Traffic Ltd
// Created: 2026-10-29
// Updated: 2026-10-29 13:48 GMT (EXP-01B.3 four-family ingestion)
// Purpose: HMRC source checks, candidate review and effective-dated tenant policies.
// Security: Backend admin/tenant checks are authoritative. This component only displays controls.
// Compatibility: Preserves existing modes, routes, Adopt HMRC and override workflow.
// Theme: General and action buttons use canonical --btn-general-* and --btn-action-*
//        CSS variables configured by SystemPage/White Label, including hover/click.
//        No hard-coded button palettes; theme context covers text/panels/inputs.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../api/axiosInstance";
import { useTheme } from "../context/ThemeContext.jsx";

const GROUPS = [
  { id: "personal_vehicle_mileage", label: "Personal vehicle mileage", description: "Own vehicle and qualifying passenger allowances" },
  { id: "company_vehicle_fuel", label: "Company vehicle fuel", description: "Petrol, diesel, LPG and electric charging" },
  { id: "domestic_subsistence", label: "Domestic subsistence", description: "Journey duration and meal allowances" },
  { id: "overnight_allowances", label: "Overnight allowances", description: "Qualifying UK and overseas incidental costs" },
  { id: "overseas_subsistence", label: "Overseas subsistence", description: "Country and currency-specific rates" },
  { id: "vat_recovery", label: "VAT & recovery guidance", description: "VAT evidence, rate and recoverability rules" },
];

function amount(value, unit) {
  if (value === null || value === undefined || value === "") return "—";
  const num = Number(value);
  const currency = String(unit || "GBP").split("/")[0];
  return currency === "percent" ? `${num.toFixed(2)}%` : `${currency} ${num.toFixed(2)}`;
}

function money(value) {
  if (value === null || value === undefined || value === "") return "—";
  return `£${Number(value).toFixed(2)}`;
}

export default function HmrcReferencePanel({ mode = "policies" }) {
  const { theme } = useTheme();
  const [rows, setRows] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [pref, setPref] = useState({ auto_adopt_new_rates: false });
  const [query, setQuery] = useState("");
  const [family, setFamily] = useState("all");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [check, setCheck] = useState(null);
  const [checkResult, setCheckResult] = useState(null);

  const panel = { background: theme.card_bg || theme.background_color || "transparent", color: theme.text_color || "inherit", border: `1px solid ${theme.border_color || "#ccc"}`, borderRadius: 8, padding: 16, marginTop: 12 };
  // Button appearance comes from Vision's White Label CSS variables, not theme.primary_color.
  // CSS pseudo-classes preserve configured normal / hover / click states.
  const actionClass = "vision-hmrc-button vision-hmrc-button-action";
  const generalClass = "vision-hmrc-button vision-hmrc-button-general";
  const input = { padding: 8, borderRadius: 5, border: `1px solid ${theme.border_color || "#aaa"}`, color: theme.text_color || "inherit", backgroundColor: theme.input_bg || theme.card_bg || "transparent" };
  const dim = { color: theme.text_secondary || theme.muted_text_color || theme.text_color || "inherit", opacity: 0.75 };
  const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,175px),1fr))", gap: 10 };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, p, c] = await Promise.all([
        api.get("/integrations/hmrc/rates"),
        api.get("/integrations/hmrc/preferences"),
        api.get("/integrations/hmrc/candidates"),
      ]);
      setRows((r.data?.rates || []).map(x => ({ ...x, draftAdopt: !!x.adopt_hmrc, draftOverride: x.override_rate ?? "" })));
      setPref(p.data || { auto_adopt_new_rates: false });
      setCandidates(c.data?.candidates || []);
    } catch (e) {
      setError(e.response?.data?.detail || "Unable to load HMRC reference data. Has the additive v2 migration been applied?");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  const update = (code, patch) => setRows(xs => xs.map(x => x.rate_code === code ? { ...x, ...patch } : x));
  const save = async row => {
    setBusy(row.rate_code); setError(""); setNotice("");
    try {
      await api.put(`/integrations/hmrc/rates/${encodeURIComponent(row.rate_code)}/policy`, {
        adopt_hmrc: !!row.draftAdopt,
        override_rate: row.draftAdopt ? null : String(row.draftOverride),
      });
      setNotice(`${row.name}: company rate policy saved`); await load();
    } catch (e) { setError(e.response?.data?.detail || "Could not save policy"); }
    finally { setBusy(""); }
  };
  const saveAuto = async next => {
    setBusy("auto"); setError("");
    try {
      await api.put("/integrations/hmrc/preferences", { auto_adopt_new_rates: next });
      await load(); setNotice(`Auto adoption ${next ? "enabled" : "disabled"}. Verified rates only; government effective dates are respected.`);
    } catch (e) { setError(e.response?.data?.detail || "Unable to save auto-adoption preference"); }
    finally { setBusy(""); }
  };
  const sourceTest = async () => {
    setBusy("source"); setError("");
    try { const r = await api.get("/integrations/hmrc/source-check"); setCheck(r.data); setNotice("GOV.UK metadata checked; reference rates unchanged."); }
    catch (e) { setError(e.response?.data?.detail || "Source check failed"); }
    finally { setBusy(""); }
  };
  const ingest = async () => {
    setBusy("ingest"); setError("");
    try {
      const r = await api.post("/integrations/hmrc/check/advisory-fuel");
      setCheckResult(r.data); setNotice(`Checked advisory fuel rates. ${r.data?.new_candidates || 0} new candidates staged for review.`);
      await load();
    } catch (e) { setError(e.response?.data?.detail || "Fuel rate check failed; no rates activated"); }
    finally { setBusy(""); }
  };
  const checkAdditional = async dataset => {
    setBusy(`check:${dataset}`); setError(""); setNotice("");
    try {
      const r = await api.post(`/integrations/hmrc/check/${encodeURIComponent(dataset)}`);
      setCheckResult(r.data);
      setNotice(`${dataset.replaceAll("_", " ")}: ${r.data?.parsed_rates || 0} recognised, ${r.data?.new_candidates || 0} new staged. Publish reviewed candidates separately.`);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || `Could not parse ${dataset}; no values activated`);
    } finally { setBusy(""); }
  };
  const publish = async candidate => {
    setBusy(`publish:${candidate.id}`); setError("");
    try {
      await api.post(`/integrations/hmrc/candidates/${candidate.id}/publish`);
      setNotice(`${candidate.name}: verified and published for ${candidate.valid_from}.`); await load();
    } catch (e) { setError(e.response?.data?.detail || "Unable to publish candidate"); }
    finally { setBusy(""); }
  };
  const visible = useMemo(() => rows.filter(x => (family === "all" || x.family === family) &&
    `${x.name} ${x.rate_code} ${x.unit}`.toLowerCase().includes(query.toLowerCase())), [rows, family, query]);
  const pending = candidates.filter(x => x.verification_status === "pending");

  return <div className="vision-hmrc-panel" style={panel}>
    <style>{`
      .vision-hmrc-panel .vision-hmrc-button {
        appearance: none; border: 0; border-radius: 8px;
        padding: 9px 13px; min-height: 38px;
        font: inherit; font-family: ${JSON.stringify(theme.font_body || 'inherit')};
        font-weight: 600; cursor: pointer; color: var(--btn-text-color, #fff);
        transition: background-color 120ms ease, box-shadow 120ms ease, opacity 120ms ease;
      }
      .vision-hmrc-panel .vision-hmrc-button-general { background: var(--btn-general-normal, #222); }
      .vision-hmrc-panel .vision-hmrc-button-general:hover:not(:disabled) { background: var(--btn-general-hover, var(--btn-general-normal, #222)); }
      .vision-hmrc-panel .vision-hmrc-button-general:active:not(:disabled) { background: var(--btn-general-click, var(--btn-general-normal, #222)); }
      .vision-hmrc-panel .vision-hmrc-button-action { background: var(--btn-action-normal, #1f7a8c); }
      .vision-hmrc-panel .vision-hmrc-button-action:hover:not(:disabled) { background: var(--btn-action-hover, var(--btn-action-normal, #1f7a8c)); }
      .vision-hmrc-panel .vision-hmrc-button-action:active:not(:disabled) { background: var(--btn-action-click, var(--btn-action-normal, #1f7a8c)); }
      .vision-hmrc-panel .vision-hmrc-button:disabled { cursor: not-allowed; opacity: .55; }
      .vision-hmrc-panel .vision-hmrc-button:focus-visible { outline: 2px solid currentColor; outline-offset: 2px; }
      .vision-hmrc-panel .vision-hmrc-button[aria-pressed="true"] { box-shadow: inset 0 0 0 2px currentColor; }
      .vision-hmrc-panel .vision-hmrc-families { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 205px), 1fr)); gap: 9px; margin: 14px 0; }
      .vision-hmrc-panel .vision-hmrc-family { min-width: 0; text-align: center; overflow-wrap: anywhere; }
      .vision-hmrc-panel .vision-hmrc-family small { display: block; margin-top: 2px; opacity: .8; font-weight: 400; }
      .vision-hmrc-panel .vision-hmrc-rates th, .vision-hmrc-panel .vision-hmrc-rates td { vertical-align: middle; }
      @media (max-width: 600px) {
        .vision-hmrc-panel .vision-hmrc-families { grid-template-columns: repeat(auto-fit, minmax(min(100%, 145px), 1fr)); }
        .vision-hmrc-panel .vision-hmrc-button { min-height: 44px; }
      }
    `}</style>
    <h3 style={{ marginTop: 0, fontFamily: theme.font_header }}>
      {mode === "integration" ? "HMRC Reference Data Integration" : "Expense Policies — Rates & Allowances"}
    </h3>
    <p style={{ fontSize: 13, ...dim }}>Government data is a reference, not automatic reimbursement permission. Only reviewed rate versions can be adopted; company overrides always take precedence.</p>
    {error && <p role="alert" style={{ color: theme.danger_color || "#ad2929" }}>{error}</p>}
    {notice && <p role="status" style={{ color: theme.success_color || theme.primary_color }}>{notice}</p>}
    {mode === "integration" ? <>
      <div style={grid}>
        <button type="button" className={generalClass} disabled={!!busy} onClick={sourceTest}>{busy === "source" ? "Checking…" : "Check GOV.UK mileage source"}</button>
        <button type="button" className={actionClass} disabled={!!busy} onClick={ingest}>{busy === "ingest" ? "Reading government tables…" : "Check & stage advisory fuel rates"}</button>
        {GROUPS.filter(g => !["personal_vehicle_mileage","company_vehicle_fuel"].includes(g.id)).map(g => (
          <button type="button" key={g.id} className={actionClass} disabled={!!busy} onClick={() => checkAdditional(g.id)}>
            {busy === `check:${g.id}` ? "Checking official source…" : `Check & stage ${g.label}`}
          </button>
        ))}
      </div>
      <p style={{ fontSize: 12, ...dim }}>The fuel checker rejects unrecognised table formats instead of guessing rates. New candidates require an explicit Verify & Publish action.</p>
      {check && <div style={{ fontSize: 12, overflowWrap: "anywhere" }}>Mileage page update: {check.public_updated_at || "Not supplied"}<div>SHA-256: <code>{check.content_hash}</code></div></div>}
      {checkResult && <p>Latest {checkResult.dataset || "fuel"} check: {checkResult.parsed_rates} recognised reference rates; {checkResult.new_candidates} newly staged.</p>}
      <h4 style={{ fontFamily: theme.font_header }}>Candidate review ({pending.length} awaiting approval)</h4>
      {pending.length === 0 ? <p style={dim}>No pending reference rates.</p> : <div style={{ display: "grid", gap: 8 }}>
        {pending.map(c => <div key={c.id} style={{ ...panel, marginTop: 0, padding: 10, ...grid, alignItems: "center" }}>
          <div><strong>{c.name}</strong><div style={{ fontSize: 12, ...dim }}>{c.valid_from} · {c.rate_code}</div></div>
          <strong>{amount(c.reference_rate,c.unit)}</strong>
          <a href={c.source_url} target="_blank" rel="noreferrer">Official GOV.UK source</a>
          <button type="button" className={actionClass} disabled={!!busy} onClick={() => publish(c)}>{busy === `publish:${c.id}` ? "Publishing…" : "Verify & Publish"}</button>
        </div>)}
      </div>}
      <p style={{ fontSize: 12, ...dim }}>All additional families use strict GOV.UK parsing and require manual review. Unsupported layouts fail closed; VAT percentages are references, not automatic recovery decisions. Overseas coverage is limited to unambiguously parsed currencies and cities.</p>
    </> : <>
      <div style={{ ...panel, marginTop: 0, marginBottom: 16 }}>
        <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
          <input type="checkbox" checked={!!pref.auto_adopt_new_rates} disabled={!!busy}
            onChange={e => saveAuto(e.target.checked)} />
          <span><strong>Auto Adopt new rates on Government effective date</strong><div style={{ fontSize: 12, ...dim, marginTop: 4 }}>
            When enabled, published and verified reference rates become the HMRC reference on their effective date. Company overrides are retained. No historical approved expenses are recalculated.</div></span>
        </label>
      </div>
      <input type="search" style={{ ...input, width: "100%", maxWidth: 450, boxSizing: "border-box" }} value={query} onChange={e => setQuery(e.target.value)} placeholder="Search rates, categories or codes…" />
      <div className="vision-hmrc-families" role="group" aria-label="Filter HMRC rate families">
        <button type="button" className={`${generalClass} vision-hmrc-family`} aria-pressed={family === "all"} onClick={() => setFamily("all")}>All rate families<small>{rows.length} rates</small></button>
        {GROUPS.map(g => <button type="button" key={g.id} className={`${generalClass} vision-hmrc-family`} aria-pressed={family === g.id} onClick={() => setFamily(g.id)} title={g.description}>
          <span>{g.label}</span><small>{rows.filter(r => r.family === g.id).length} rates</small>
        </button>)}
      </div>
      {loading ? <p>Loading reference data…</p> : visible.length === 0 ? <p style={dim}>No rates in this group yet. Missing datasets have not been fabricated.</p> :
        <div style={{ overflowX: "auto" }}>
          <table className="vision-hmrc-rates" style={{ width: "100%", minWidth: 640, borderCollapse: "collapse", color: "inherit" }}>
            <thead><tr>{["Reference", "HMRC", "Adopt HMRC", "Override", "Effective", "Action"].map(t =>
              <th key={t} style={{ textAlign: "left", padding: 8, borderBottom: `1px solid ${theme.border_color || "#aaa"}` }}>{t}</th>)}</tr></thead>
            <tbody>{visible.map(x => <tr key={x.rate_code}>
              <td style={{ padding: 8, borderBottom: `1px solid ${theme.border_color || "#ddd"}` }}><strong>{x.name}</strong><div style={{ fontSize: 11, ...dim }}>{x.unit} · {(["domestic_subsistence","overnight_allowances","overseas_subsistence","vat_recovery"].includes(x.family)) ? "catalogued as of" : "from"} {x.valid_from}</div></td>
              <td style={{ padding: 8 }}>{amount(x.reference_rate,x.unit)}</td>
              <td style={{ padding: 8 }}><input type="checkbox" checked={!!x.draftAdopt} aria-label={`Adopt HMRC for ${x.name}`} onChange={e => update(x.rate_code, { draftAdopt: e.target.checked })} /></td>
              <td style={{ padding: 8 }}><input type="number" min="0" step="0.01" style={{ ...input, width: 88 }} disabled={x.draftAdopt} value={x.draftOverride} onChange={e => update(x.rate_code, { draftOverride: e.target.value })} /></td>
              <td style={{ padding: 8 }}>{amount(x.draftAdopt ? x.reference_rate : x.draftOverride,x.unit)}</td>
              <td style={{ padding: 8 }}><button type="button" className={actionClass} disabled={!!busy || (!x.draftAdopt && x.draftOverride === "")} onClick={() => save(x)}>{busy === x.rate_code ? "Saving…" : "Save"}</button></td>
            </tr>)}</tbody>
          </table>
        </div>}
      <p style={{ fontSize: 12, ...dim }}>The auto-adoption control only applies to verified, published versions and does not override tenant-entered company rates. No employee claim or payment calculation is performed here.</p>
    </>}
  </div>;
}
